import { AppButton, DotMatrix } from '@clickguard/design-system'
import { FiArrowLeft } from 'react-icons/fi'
import {
	Platform,
	AccountLinkType,
	AccountSelectionState,
	MCCAccountLinkDTO
} from './types'
import { AuthorizationDropdown } from './authorization-dropdown'
import { AccountDropdown } from './account-dropdown'
import { MccClientSelector } from './mcc-client-selector'
import { PlatformToggles } from './platform-toggles'
import { InviteTeamMember } from './invite-team-member'
import { VisitKnowledgeBase } from './visit-knowledge-base'
import { PixelDropdown } from './pixel-dropdown'

interface AccountSelectionViewProps {
	isOnboarding?: boolean
	platform: Platform
	accountLink: AccountLinkType | null
	availableAccountLinks: AccountLinkType[]
	selectedAccountLinkId: string | null
	accountSelection: AccountSelectionState
	mccLink: MCCAccountLinkDTO | null
	accountOptions: any[]
	pixelOptions: Array<{ id: string; name: string }>
	loading: boolean
	error: string | null
	onBack: () => void
	onAuthorizationChange: (link: AccountLinkType) => void
	onAccountChange: (selected: any) => void
	onMccClientChange: (client: any) => void
	onToggleChange: (updates: Partial<AccountSelectionState>) => void
	onPixelChange: (pixelId: string | null) => void
	onCreateNewAuthorization: () => void
	onSubmit: () => void
	getConnectedAccountInfo: () => string | null
	mccClientLoading: boolean
}

const platformNames: Record<Platform, string> = {
	GADS: 'Google Ads',
	FADS: 'Meta Ads',
	BADS: 'Microsoft Ads'
}

export const AccountSelectionView = ({
	platform,
	accountLink,
	availableAccountLinks,
	selectedAccountLinkId,
	accountSelection,
	mccLink,
	accountOptions,
	pixelOptions,
	loading,
	error,
	onBack,
	onAuthorizationChange,
	onAccountChange,
	onMccClientChange,
	onToggleChange,
	onPixelChange,
	onCreateNewAuthorization,
	onSubmit,
	getConnectedAccountInfo,
	isOnboarding = true,
	mccClientLoading
}: AccountSelectionViewProps) => {
	return (
		<div className="flex flex-col gap-[24px]">
			<div className="flex flex-col gap-[16px]">
				<button
					type="button"
					onClick={onBack}
					className="flex w-fit items-center gap-[8px] rounded-full text-[16px] font-semibold leading-[22px] text-t-desc-gray hover:text-t-default"
				>
					<FiArrowLeft className="h-5 w-5" />
					Back
				</button>
				<div className="flex flex-col gap-[16px]">
					<h1 className="whitespace-nowrap text-[24px] font-semibold leading-[30px] text-t-default">
						Connect your {platformNames[platform]} account
					</h1>
					<p className="text-[16px] leading-[22px] text-t-checkbox-unactive pb-[8px]">
						Select the first ad account you want to connect. You can add
						additional platforms and ad accounts later.
					</p>
				</div>
			</div>

			<AuthorizationDropdown
				platform={platform}
				availableAccountLinks={availableAccountLinks}
				selectedAccountLinkId={selectedAccountLinkId}
				accountLink={accountLink}
				loading={loading}
				onChange={onAuthorizationChange}
				onCreateNewAuthorization={onCreateNewAuthorization}
				getConnectedAccountInfo={getConnectedAccountInfo}
			/>

			{accountLink && (
				<AccountDropdown
					platform={platform}
					accountOptions={accountOptions}
					accountSelection={accountSelection}
					loading={loading}
					mccLink={mccLink}
					onChange={onAccountChange}
				/>
			)}

			{mccLink && (
				<MccClientSelector
					mccLink={mccLink}
					selectedClient={accountSelection.client}
					loading={loading}
					onChange={onMccClientChange}
				/>
			)}

			{platform === 'FADS' && accountLink && accountSelection.selected && (
				<PixelDropdown
					accountSelection={accountSelection}
					pixelOptions={pixelOptions}
					loading={loading}
					onChange={onPixelChange}
				/>
			)}

			{(accountLink || mccLink) &&
				accountSelection.selected &&
				!mccClientLoading && (
					<PlatformToggles
						platform={platform}
						accountSelection={accountSelection}
						onChange={onToggleChange}
					/>
				)}

			{error && (
				<div className="rounded-lg border border-red-200 bg-red-50 p-3">
					<p className="text-sm text-red-600">{error}</p>
				</div>
			)}
			{!!mccClientLoading && (
				<div className="flex items-center justify-center gap-2 py-4">
					<DotMatrix size="sm" />
					<p className="text-[14px] leading-[18px] text-t-checkbox-unactive">
						Loading your MCC account...
					</p>
				</div>
			)}

			<AppButton
				variant="marketing"
				size="lg"
				fullWidth
				label="Continue"
				isLoading={loading}
				loader="dots"
				disabled={
					loading ||
					mccClientLoading ||
					(mccLink
						? !accountSelection.client?.account_number
						: !accountSelection.selected)
				}
				onClick={onSubmit}
			/>

			{isOnboarding && <InviteTeamMember step="ad_account" />}
			{!isOnboarding && <VisitKnowledgeBase />}
		</div>
	)
}
